import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button, DataSection, FieldError, FieldRow, Input, Label } from '@sentinel/ui';
import { nameSchema, type NameInput } from '../schema';
import { useUpdateName } from '../use-profile-mutations';
import type { SectionFeedback } from '../types';
import { getStatus } from '../error-accessors';

export interface NameSectionProps {
  currentName: string;
  onSaved: () => void;
  feedback: SectionFeedback;
}

export function NameSection({ currentName, onSaved, feedback }: NameSectionProps) {
  const { t } = useTranslation('profile');
  const update = useUpdateName();
  const form = useForm<NameInput>({
    resolver: zodResolver(nameSchema),
    defaultValues: { name: currentName },
  });

  async function onSubmit(values: NameInput) {
    feedback.clear();
    try {
      const name = values.name.trim();
      await update.mutateAsync({ name });
      form.reset({ name });
      feedback.success(t('name_saved'));
      onSaved();
    } catch (e) {
      // Step-up middleware handles 403 step_up_required transparently. This
      // route carries no step-up guard, but a 403 reaching here still means
      // "leave the form alone" under the house contract.
      if (getStatus(e) === 403) return;
      feedback.error(t('generic_error'));
    }
  }

  const nameErrorRaw = form.formState.errors.name?.message;
  const nameErrorMsg =
    nameErrorRaw === 'name_required'
      ? t('name_required')
      : nameErrorRaw === 'name_too_long'
        ? t('name_too_long')
        : nameErrorRaw;

  return (
    <DataSection
      testId="profile-name"
      title={t('name_section_title')}
      description={t('name_section_body')}
    >
      <form
        data-testid="profile-name-form"
        onSubmit={form.handleSubmit(onSubmit)}
        style={{ display: 'grid', gap: '0.75rem' }}
      >
        <FieldRow>
          <Label htmlFor="profile-name-input">{t('name_label')}</Label>
          <Input
            id="profile-name-input"
            data-testid="profile-name-input"
            autoComplete="name"
            aria-invalid={form.formState.errors.name ? 'true' : undefined}
            {...form.register('name')}
          />
          <FieldError message={nameErrorMsg} />
        </FieldRow>
        <Button
          type="submit"
          data-testid="profile-name-submit"
          disabled={update.isPending || !form.formState.isDirty}
        >
          {t('name_submit')}
        </Button>
      </form>
    </DataSection>
  );
}
